import { useState, useCallback } from 'react';
import { DiscoverUser, DiscoverFilters, DiscoverStats, DiscoverAction, SavedUser } from '../types/discover';
import { User } from './useAuth';
import { discoverAPI } from '../services/discoverService';

export const useDiscover = () => {
  const [users, setUsers] = useState<DiscoverUser[]>([]);
  const [suggestions, setSuggestions] = useState<DiscoverUser[]>([]);
  const [savedUsers, setSavedUsers] = useState<SavedUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<DiscoverFilters>({});
  const [stats, setStats] = useState<DiscoverStats | null>(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
    hasMore: false,
    hasPrev: false
  });

  // Load discovery users
  const loadUsers = useCallback(async (
    currentUser: User, 
    newFilters?: DiscoverFilters,
    page: number = 1,
    reset: boolean = true,
    limit?: number // Optional limit override
  ) => {
    setLoading(true);
    setError(null);
    
    try {
      // Use newFilters if provided, otherwise use current filters
      const filtersToUse = newFilters !== undefined ? newFilters : filters;
      // Use provided limit or current pagination limit
      const limitToUse = limit !== undefined ? limit : pagination.limit;
      
      const response = await discoverAPI.discoverUsers(
        currentUser, 
        filtersToUse, 
        page, 
        limitToUse
      );
      
      if (response.success && response.data) {
        // Always replace users when loading (backend handles pagination)
        setUsers(response.data);
        
        // Update filters if new ones provided
        if (newFilters !== undefined) {
          setFilters(newFilters);
        }
        
        // Update pagination from backend response
        if (response.pagination) {
          setPagination({
            page: response.pagination.page,
            limit: limitToUse, // Use the limit we actually sent
            total: response.pagination.total,
            totalPages: Math.ceil(response.pagination.total / limitToUse) || 0,
            hasMore: response.pagination.hasMore || false,
            hasPrev: response.pagination.page > 1
          });
        }
      } else {
        setError(response.message || 'Failed to load users');
        // Clear users on error
        if (reset || page === 1) {
          setUsers([]);
        }
      }
    } catch (err) {
      setError((err as Error).message);
      // Clear users on error
      if (reset || page === 1) {
        setUsers([]);
      }
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.limit]);

  // Load more users (pagination) - Note: Backend handles pagination, so this loads next page
  const loadMoreUsers = useCallback(async (currentUser: User) => {
    if (pagination.hasMore && !loading) {
      await loadUsers(currentUser, filters, pagination.page + 1, false);
    }
  }, [loadUsers, filters, pagination.hasMore, pagination.page, loading]);

  // Go to specific page
  const goToPage = useCallback(async (currentUser: User, page: number) => {
    if (page >= 1 && page <= pagination.totalPages && !loading) {
      await loadUsers(currentUser, filters, page, true);
    }
  }, [loadUsers, filters, pagination.totalPages, loading]);

  // Go to next page
  const goToNextPage = useCallback(async (currentUser: User) => {
    if (pagination.hasMore && !loading) {
      await loadUsers(currentUser, filters, pagination.page + 1, true);
    }
  }, [loadUsers, filters, pagination.hasMore, pagination.page, loading]);

  // Go to previous page
  const goToPrevPage = useCallback(async (currentUser: User) => {
    if (pagination.page > 1 && !loading) {
      await loadUsers(currentUser, filters, pagination.page - 1, true);
    }
  }, [loadUsers, filters, pagination.page, loading]);

  // Set items per page
  const setItemsPerPage = useCallback(async (currentUser: User, newLimit: number) => {
    if (!loading && [10, 20, 50, 200].includes(newLimit)) {
      // Pass the new limit directly to loadUsers
      await loadUsers(currentUser, filters, 1, true, newLimit);
    }
  }, [loadUsers, filters, loading]);

  // Load suggestions
  const loadSuggestions = useCallback(async (currentUser: User, limit: number = 10) => {
    try {
      const response = await discoverAPI.getSuggestions(currentUser, limit);
      
      if (response.success && response.data) {
        setSuggestions(response.data);
      } else {
        setError(response.message || 'Failed to load suggestions');
      }
    } catch (err) {
      setError((err as Error).message);
    }
  }, []);

  // Load saved users
  const loadSavedUsers = useCallback(async () => {
    try {
      const response = await discoverAPI.getSavedUsers();
      
      if (response.success && response.data) {
        setSavedUsers(response.data);
      } else {
        setError(response.message || 'Failed to load saved users');
      }
    } catch (err) {
      setError((err as Error).message);
    }
  }, []);

  // Load discovery statistics
  const loadStats = useCallback(async () => {
    try {
      const response = await discoverAPI.getDiscoverStats();
      
      if (response.success && response.data) {
        setStats(response.data);
      } else {
        setError(response.message || 'Failed to load statistics');
      }
    } catch (err) {
      setError((err as Error).message);
    }
  }, []);

  // Perform discovery action
  const performAction = useCallback(async (action: DiscoverAction) => {
    try {
      const response = await discoverAPI.performAction(action);
      
      if (response.success) {
        // Update local state based on action
        switch (action.type) {
          case 'like':
            setUsers(prev => prev.map(user => 
              user.id === action.userId 
                ? { ...user, hasLiked: true, hasPassed: false }
                : user
            ));
            break;
          case 'pass':
            setUsers(prev => prev.map(user => 
              user.id === action.userId 
                ? { ...user, hasPassed: true, hasLiked: false }
                : user
            ));
            break;
          case 'save':
            setUsers(prev => prev.map(user => 
              user.id === action.userId 
                ? { ...user, hasSaved: true }
                : user
            ));
            break;
          case 'unsave':
            setUsers(prev => prev.map(user => 
              user.id === action.userId 
                ? { ...user, hasSaved: false }
                : user
            ));
            break;
          case 'report':
            setUsers(prev => prev.map(user => 
              user.id === action.userId 
                ? { ...user, hasReported: true }
                : user
            ));
            break;
        }
        
        return { success: true };
      } else {
        setError(response.message || 'Failed to perform action');
        return { success: false, error: response.message };
      }
    } catch (err) {
      const errorMessage = (err as Error).message;
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, []);

  // Save user
  const saveUser = useCallback(async (userId: string) => {
    try {
      const response = await discoverAPI.saveUser(userId);
      
      if (response.success) {
        setUsers(prev => prev.map(user => 
          user.id === userId 
            ? { ...user, hasSaved: true }
            : user
        ));
        return { success: true };
      } else {
        setError(response.message || 'Failed to save user');
        return { success: false, error: response.message };
      }
    } catch (err) {
      const errorMessage = (err as Error).message;
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, []);

  // Unsave user
  const unsaveUser = useCallback(async (userId: string) => {
    try {
      const response = await discoverAPI.unsaveUser(userId);
      
      if (response.success) {
        setUsers(prev => prev.map(user => 
          user.id === userId 
            ? { ...user, hasSaved: false }
            : user
        ));
        setSavedUsers(prev => prev.filter(saved => saved.userId !== userId));
        return { success: true };
      } else {
        setError(response.message || 'Failed to unsave user');
        return { success: false, error: response.message };
      }
    } catch (err) {
      const errorMessage = (err as Error).message;
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, []);

  // Report user
  const reportUser = useCallback(async (userId: string, reason: string, details?: string) => {
    try {
      const response = await discoverAPI.reportUser(userId, reason, details);
      
      if (response.success) {
        setUsers(prev => prev.map(user => 
          user.id === userId 
            ? { ...user, hasReported: true }
            : user
        ));
        return { success: true };
      } else {
        setError(response.message || 'Failed to report user');
        return { success: false, error: response.message };
      }
    } catch (err) {
      const errorMessage = (err as Error).message;
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, []);

  // Send match invitation
  const sendMatchInvitation = useCallback(async (targetUserId: string, message?: string) => {
    try {
      const response = await discoverAPI.sendMatchInvitation(targetUserId, message);
      
      if (response.success) {
        return { success: true, data: response.data };
      } else {
        setError(response.message || 'Failed to send match invitation');
        return { success: false, error: response.message };
      }
    } catch (err) {
      const errorMessage = (err as Error).message;
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, []);

  // Apply filters
  const applyFilters = useCallback((newFilters: DiscoverFilters) => {
    setFilters(newFilters);
  }, []);

  // Clear filters
  const clearFilters = useCallback(() => {
    setFilters({});
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Reset pagination
  const resetPagination = useCallback(() => {
    setPagination({
      page: 1,
      limit: 20,
      total: 0,
      totalPages: 0,
      hasMore: false,
      hasPrev: false
    });
  }, []);

  // Get match invitations
  const getMatchInvitations = useCallback(async () => {
    try {
      const response = await discoverAPI.getMatchInvitations();
      
      if (response.success && response.data) {
        return { success: true, data: response.data };
      } else {
        setError(response.message || 'Failed to get match invitations');
        return { success: false, error: response.message };
      }
    } catch (err) {
      const errorMessage = (err as Error).message;
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, []);

  // Respond to match invitation
  const respondToInvitation = useCallback(async (invitationId: string, response: 'accepted' | 'declined') => {
    try {
      const result = await discoverAPI.respondToInvitation(invitationId, response);
      
      if (result.success) {
        return { success: true, data: result.data };
      } else {
        setError(result.message || 'Failed to respond to invitation');
        return { success: false, error: result.message };
      }
    } catch (err) {
      const errorMessage = (err as Error).message;
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, []);

  return {
    // State
    users,
    suggestions,
    savedUsers,
    loading,
    error,
    filters,
    stats,
    pagination,
    
    // Actions
    loadUsers,
    loadMoreUsers,
    loadSuggestions,
    loadSavedUsers,
    loadStats,
    performAction,
    saveUser,
    unsaveUser,
    reportUser,
    sendMatchInvitation,
    applyFilters,
    clearFilters,
    clearError,
    resetPagination,
    goToPage,
    goToNextPage,
    goToPrevPage,
    setItemsPerPage,
    getMatchInvitations,
    respondToInvitation
  };
};
